/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DATA_SET_PAGINATION_COUNT, SORT_DIRECTION_INDICATOR } from 'constants/dataSets';
import { DEFAULT_ORDER_BY } from 'constants/general';
import { useGetSurveysQuery, useShareInsightMutation } from 'store/slices/survey/apis/surveyApi';

import { ORDER_KEY, ORDER_TYPE, SORT } from '../constants/formFields';
import { ORDERS } from '../constants/interfaces';
import { SURVEY_STATUS } from '../constants/materialAssessment';
import i18n from '../i18n';
import useMaterialityAssessmentSurveyListForm
  from '../pages/MaterialAssessmentPages/MaterialAssessmentSurveyCreation/hooks/useMaterialityAssessmentSurveyListForm';

import type { TOrderByIdentifier } from '../constants/interfaces';
import type IQuestionsItem from '../store/slices/survey/interfaces/IQuestionsItem';

const useMaterialityAssessmentQuestion = (insightType: string) => {
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [listSurveys, setListSurveys] = useState<IQuestionsItem[]>([]);
  const [offsetMain, setOffsetMain] = useState(0);
  const { recordId } = useParams();

  const {
    control, getValues, setValue,
  } = useMaterialityAssessmentSurveyListForm();

  const { data, isFetching: isSurveyListLoading } = useGetSurveysQuery({
    orderBy: orderBy ? [orderBy] : undefined,
    offset: offsetMain,
    maId: Number(recordId),
    status: SURVEY_STATUS.DONE,
    limit: DATA_SET_PAGINATION_COUNT,
  });

  const [shareInsight] = useShareInsightMutation();

  const formattedVCList = (vcIdList: number[], emailList: { email: string, respondentType: string }[]) => {
    const result: { valueChainMapId: number, respondentType: string, email: string }[] = [];

    vcIdList.forEach((vcId) => {
      emailList.forEach((user) => {
        result.push({
          valueChainMapId: vcId,
          respondentType: user.respondentType,
          email: user.email,
        });
      });
    });

    return result;
  };

  const handleShareSurvey = (
    emails: { email: string, respondentType: string }[],
    valueChainMapId: number,
    listQuestionIds: number[],
  ) => {
    const respondents = formattedVCList([valueChainMapId], emails);

    shareInsight({
      insightType,
      questionListIds: listQuestionIds,
      respondents,
    })
      .unwrap()
      .then(() => {
        toast.success(i18n.t('notifications.surveySharedSuccessfully'));
      })
      .catch(() => {
        toast.error(i18n.t('notifications.errorSharingSurvey'));
      });
  };

  const { meta, items } = data?.data || {};
  const isEmptyList = (!data?.data?.meta?.count || data?.data?.items?.length === 0) && !isSurveyListLoading;
  const hasMore = data?.data?.meta.hasNext || false;

  useEffect(() => {
    if (items && items.length > 0) {
      setListSurveys((prev) => {
        const prevIds = new Set(prev.map((item) => item.id));
        const newItems = items.filter((item) => !prevIds.has(item.id));

        if (offsetMain === 0) {
          return items;
        }

        return [...prev, ...newItems];
      });
    }
  }, [items, offsetMain]);

  const onSortChange = (orderByIdentifier: TOrderByIdentifier) => {
    const { sort: { orderKey, orderType } } = getValues();
    const isAsc = orderKey === orderByIdentifier && orderType === ORDERS.asc;
    setValue(SORT, { [ORDER_KEY]: orderByIdentifier, [ORDER_TYPE]: isAsc ? ORDERS.desc : ORDERS.asc });
    const order = isAsc ? `${SORT_DIRECTION_INDICATOR}${orderByIdentifier}` : orderByIdentifier;
    setOrderBy(order);
  };

  const loadMoreQuestions = () => {
    if (data?.data.meta.hasNext) {
      setOffsetMain((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  return {
    isEmptyList,
    isLoading: isSurveyListLoading,
    control,
    onSortChange,
    itemsList: listSurveys || [],
    hasNext: !!meta?.hasNext,
    loadMoreQuestions,
    hasMore,
    handleShareSurvey,
  };
};

export default useMaterialityAssessmentQuestion;
